import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ColumnDef, RawDataTableProps } from '../types/table.types';
import { ChevronLeftIcon, ChevronRightIcon, ArrowUpIcon, ArrowDownIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { Package } from 'lucide-react';

/**
 * Reusable table component for displaying raw data
 * Features:
 * - Auto-column detection from data
 * - Sortable columns
 * - Pagination support
 * - Loading/error/empty states
 * - Responsive design
 */
const RawDataTable: React.FC<RawDataTableProps> = ({
  data,
  columns,
  loading = false,
  error = null,
  emptyMessage = 'Aucune donnée disponible',
  pagination,
  maxHeight = '600px',
  className = '',
  onRowClick,
  stickyHeader = true,
}) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableElement>(null);
  const previousPageRef = useRef<number | undefined>(pagination?.currentPage);
  const previousLoadingRef = useRef<boolean>(loading);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const targetScrollPositionRef = useRef<number | null>(null);
  const isRestoringScrollRef = useRef<boolean>(false);

  // Store target scroll position and start preventing scroll-to-top when page changes
  useEffect(() => {
    if (pagination && previousPageRef.current !== undefined && 
        previousPageRef.current !== pagination.currentPage) {
      // Page changed - store current table position
      if (tableContainerRef.current) {
        const rect = tableContainerRef.current.getBoundingClientRect();
        targetScrollPositionRef.current = rect.top + window.scrollY;
        isRestoringScrollRef.current = true;
        
        // Immediately scroll to prevent any scroll-to-top
        window.scrollTo({
          top: targetScrollPositionRef.current,
          behavior: 'auto'
        });
      }
    }
    previousPageRef.current = pagination?.currentPage;
  }, [pagination?.currentPage]);

  // Continuously restore scroll position during loading to prevent scroll-to-top
  useEffect(() => {
    if (isRestoringScrollRef.current && loading && targetScrollPositionRef.current !== null) {
      // Clear any existing interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
      
      // Continuously restore scroll position while loading
      scrollIntervalRef.current = setInterval(() => {
        if (targetScrollPositionRef.current !== null) {
          const currentScroll = window.scrollY;
          const targetScroll = targetScrollPositionRef.current;
          
          // If we've scrolled away from target (especially if scrolled to top), restore it
          if (Math.abs(currentScroll - targetScroll) > 10) {
            window.scrollTo({
              top: targetScroll,
              behavior: 'auto'
            });
          }
        }
      }, 50); // Check every 50ms
    } else {
      // Stop the interval when not loading
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }
    
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, [loading]);

  // Final smooth scroll when data finishes loading
  useEffect(() => {
    const loadingFinished = previousLoadingRef.current && !loading;
    
    if (isRestoringScrollRef.current && loadingFinished && data && data.length > 0) {
      // Clear any existing timeout and interval
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      
      // Wait for DOM to update, then do final smooth scroll
      scrollTimeoutRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          if (tableHeaderRef.current) {
            const rect = tableHeaderRef.current.getBoundingClientRect();
            const targetPosition = rect.top + window.scrollY;
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          } else if (tableContainerRef.current) {
            const rect = tableContainerRef.current.getBoundingClientRect();
            const targetPosition = rect.top + window.scrollY;
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
          
          // Reset flags
          isRestoringScrollRef.current = false;
          targetScrollPositionRef.current = null;
        });
      }, 100);
    }
    
    // Update loading ref
    previousLoadingRef.current = loading;
    
    // Cleanup timeout on unmount
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [loading, data]);

  // Auto-detect columns from data if not provided
  const detectedColumns = useMemo<ColumnDef[]>(() => {
    if (columns) return columns;
    
    if (!data || data.length === 0) return [];
    
    // Get all unique keys from the first few rows
    const allKeys = new Set<string>();
    
    // Collect keys from all rows to handle missing fields
    data.slice(0, 10).forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });
    
    return Array.from(allKeys).map(key => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      sortable: true,
    }));
  }, [columns, data]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortKey || !data) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      
      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      // Handle different types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [data, sortKey, sortDirection]);

  // Apply pagination if provided
  // Note: When pagination prop is provided, data is already paginated by the backend
  // We only do client-side pagination if pagination prop is NOT provided
  const paginatedData = useMemo(() => {
    // If pagination prop is provided, backend handles pagination - just return sorted data
    if (pagination) return sortedData;
    
    // Otherwise, do client-side pagination (for non-paginated endpoints)
    const startIdx = 0;
    const endIdx = sortedData.length;
    return sortedData.slice(startIdx, endIdx);
  }, [sortedData, pagination]);

  // Handle column sort
  const handleSort = (column: ColumnDef) => {
    if (!column.sortable) return;
    
    if (sortKey === column.key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(column.key);
      setSortDirection('asc');
    }
  };

  // Render sort icon
  const renderSortIcon = (column: ColumnDef) => {
    if (!column.sortable) return null;
    
    if (sortKey !== column.key) {
      return <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />;
    }
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 text-blue-600" />
      : <ArrowDownIcon className="h-4 w-4 text-blue-600" />;
  };

  // Format cell value
  const formatCellValue = (value: any, column: ColumnDef, row: any): React.ReactNode => {
    if (column.render) {
      return column.render(value, row);
    }
    
    if (value == null) {
      return <span className="text-gray-400">—</span>;
    }
    
    // Format dates
    if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
      try {
        const date = new Date(value);
        return date.toLocaleDateString('fr-FR');
      } catch {
        return String(value);
      }
    }
    
    // Format numbers
    if (typeof value === 'number') {
      return value.toLocaleString('fr-FR');
    }
    
    // Format booleans
    if (typeof value === 'boolean') {
      return value ? 'Oui' : 'Non';
    }
    
    // Truncate long strings
    const strValue = String(value);
    if (strValue.length > 50) {
      return (
        <span title={strValue} className="truncate block max-w-xs">
          {strValue.substring(0, 50)}...
        </span>
      );
    }
    
    return strValue;
  };

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400 animate-pulse mb-4" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-500 mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={tableContainerRef} className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Table Container */}
      <div className="overflow-auto" style={{ maxHeight }}>
        <table ref={tableHeaderRef} className="min-w-full divide-y divide-gray-200">
          {/* Header */}
          <thead className={`bg-gray-50 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {detectedColumns.map((column) => (
                <th
                  key={column.key}
                  className={`
                    px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                    ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}
                    ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}
                    ${column.className || ''}
                  `}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column)}
                >
                  <div className={`flex items-center gap-2 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start'}`}>
                    <span>{column.label}</span>
                    {renderSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          {/* Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`
                  hover:bg-gray-50 transition-colors
                  ${onRowClick ? 'cursor-pointer' : ''}
                `}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {detectedColumns.map((column) => (
                  <td
                    key={column.key}
                    className={`
                      px-4 py-3 text-sm text-gray-900 whitespace-nowrap
                      ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}
                      ${column.className || ''}
                    `}
                  >
                    {formatCellValue(row[column.key], column, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              Page {pagination.currentPage} sur {pagination.totalPages}
            </span>
            {pagination.showPageSizeSelector && pagination.onPageSizeChange && (
              <select
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                className="ml-4 px-2 py-1 text-sm border border-gray-300 rounded-md"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            
            <span className="text-sm text-gray-700">
              {((pagination.currentPage - 1) * pagination.pageSize) + 1} - {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} sur {pagination.totalCount}
            </span>
            
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawDataTable;

